import axios from 'axios';

import { BASE_URL_V3 } from '@/constants/urls';
import TokenManager from '@/util/tokenManager';
import { getRestEndpoint } from '@/runtimeConfig';

// Not called eagerly at module scope: `@/util/tokenManager` imports
// `@/util/vexo`, which (since the in-house analytics dual-write) pulls in
// analyticsQueue → useFeatureFlag → featureFlagsAPI → this module — a
// circular require. An eager `TokenManager.getInstance()` here landed mid-
// cycle with `TokenManager` still undefined (`Cannot read properties of
// undefined (reading 'getInstance')`), breaking ~160 Jest suites that import
// this module transitively. `getInstance()` is a memoized singleton, so
// resolving it lazily at each call site (after all modules have finished
// loading) is behavior-identical.
const tokenManager = () => TokenManager.getInstance();

const axiosClient = axios.create({
  baseURL: BASE_URL_V3,
});

// No baseURL here: it is resolved per request in the interceptor below.
// Setting it at module load captures the BUILD-TIME value, which is what sent
// every REST call from a review app to whichever environment the bundle was
// compiled against.
const axiosRubyClient = axios.create();

axiosClient.interceptors.request.use(async (config) => {
  const token = tokenManager().getAccessToken();

  if (token && config.headers) {
    config.headers.Authorization = `bearer ${token}`;
  }

  return config;
});

axiosRubyClient.interceptors.request.use(async (config) => {
  // Per request, so it follows /config.json once that has loaded. The previous
  // hardcoded production fallback meant an unset variable pointed at prod
  // rather than failing.
  config.baseURL = getRestEndpoint();

  const token = tokenManager().getAccessToken();

  if (token && config.headers) {
    config.headers.Authorization = `bearer ${token}`;
  }

  if (
    config.method &&
    ['patch', 'post', 'put'].includes(config.method.toLowerCase())
  ) {
    if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }
  }

  return config;
});

axiosClient.interceptors.response.use(
  (response) => response,
  async function (error) {
    const originalRequest = error?.config;
    if (error?.response?.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;
      const newToken = await tokenManager().ensureFreshToken();
      if (newToken) {
        originalRequest.headers.Authorization = `bearer ${newToken}`;
        return axiosClient(originalRequest);
      }
      tokenManager().logout();
    }
    return Promise.reject(error);
  },
);

axiosRubyClient.interceptors.response.use(
  (response) => response,
  async function (error) {
    const originalRequest = error?.config;
    if (error?.response?.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;
      const newToken = await tokenManager().ensureFreshToken();
      if (newToken) {
        originalRequest.headers.Authorization = `bearer ${newToken}`;
        return axiosRubyClient(originalRequest);
      }
      tokenManager().logout();
    }
    return Promise.reject(error);
  },
);

export { axiosRubyClient };
export default axiosClient;
